import classNames from "classnames";
import { get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { FaWhatsapp } from "react-icons/fa";
import {
  MdFlag,
  MdFlashOn,
  MdOutlineMail,
  MdOpenInNew,
  MdPerson,
  MdPhone,
  MdPhoneMissed,
  MdHeadset,
  MdVisibility,
} from "react-icons/md";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACTIVITY_TYPES,
  ALVIO_ACTIVITY_ACTIONS,
  CALL_DIRECTIONS,
  CALL_STATUSES,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import { CALL_RECORDING_URL } from "utils/api/graphql/queries/call";
import { ensureUtcDate, formatDate } from "utils/helpers/date";
import Tooltip from "../Tooltip";

const activityLinksPropType = PropTypes.shape({
  enabled: PropTypes.bool,
  labels: PropTypes.shape({
    project: PropTypes.string,
    contact: PropTypes.string,
  }),
  onOpenProject: PropTypes.func,
  onOpenContact: PropTypes.func,
});

const ActivityDashboardLinks = ({ activity, activityLinks }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);

  if (!activityLinks?.enabled) {
    return null;
  }
  const meta = activity.dashboardMeta;
  const projectId = meta ? get(meta, "project.id") : undefined;
  const contactId = meta
    ? meta.contactId || get(meta, "project.contact.id")
    : undefined;
  const canOpenContact = Boolean(contactId);
  const canOpenProject = Boolean(projectId);

  const contactTooltipTitle = canOpenContact
    ? (activityLinks.labels?.contact ?? t("ACTIVITIES.OPEN_CONTACT"))
    : t("ACTIVITIES.LINK_DISABLED_NO_CONTACT");
  const projectTooltipTitle = canOpenProject
    ? (activityLinks.labels?.project ?? t("ACTIVITIES.OPEN_PROJECT"))
    : t("ACTIVITIES.LINK_DISABLED_NO_PROJECT");

  return (
    <div className="activities__log--dashboard-links d-flex align--center gap-4">
      <Tooltip title={contactTooltipTitle}>
        <span className="activities__log--dashboard-link-tooltip-target">
          <button
            type="button"
            className="activities__log--dashboard-link"
            onClick={() => activityLinks.onOpenContact?.(activity)}
            aria-label={activityLinks.labels?.contact}
            disabled={!canOpenContact}
          >
            <MdPerson size={18} />
          </button>
        </span>
      </Tooltip>
      <Tooltip title={projectTooltipTitle}>
        <span className="activities__log--dashboard-link-tooltip-target">
          <button
            type="button"
            className="activities__log--dashboard-link"
            onClick={() => activityLinks.onOpenProject?.(activity)}
            aria-label={activityLinks.labels?.project}
            disabled={!canOpenProject}
          >
            <MdOpenInNew size={18} />
          </button>
        </span>
      </Tooltip>
    </div>
  );
};

ActivityDashboardLinks.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
};

const formatContactUserLine = (contactUser) => {
  if (!contactUser) {
    return "";
  }
  return `${contactUser.firstname || ""} ${contactUser.lastname || ""}`.trim();
};

const ActivityIdentityNames = ({ activity, activityLinks, actorLine, t }) => {
  const contactLine = formatContactUserLine(activity.contactUser);
  if (!activityLinks?.enabled) {
    return actorLine ? <span className="name">{actorLine}</span> : null;
  }
  if (!contactLine && !actorLine) {
    return null;
  }
  if (!contactLine) {
    return actorLine ? (
      <div className="activities__log--identity-lines">
        <span className="activities__log--actor-subline">
          {t("ACTIVITIES.WITH")} {actorLine}
        </span>
      </div>
    ) : null;
  }
  return (
    <div className="activities__log--identity-lines">
      <span className="name">{contactLine}</span>
      {actorLine ? (
        <span className="activities__log--actor-subline">
          {t("ACTIVITIES.WITH")} {actorLine}
        </span>
      ) : null}
    </div>
  );
};

ActivityIdentityNames.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
  actorLine: PropTypes.string,
  t: PropTypes.func.isRequired,
};

ActivityIdentityNames.defaultProps = {
  activityLinks: undefined,
  actorLine: "",
};

/**
 * Labels for subscription-related timeline rows (fiche client + dashboard).
 * Uses broker.task DETAIL.ACTIVITIES / SUBSCRIPTION_STATUSES like task screens.
 */
const getStatusActivityDisplay = (status, tTask) => {
  const bySubscriptionStatus = {
    [SUBSCRIPTION_STATUSES.DRAFT]: {
      label: tTask("DETAIL.ACTIVITIES.QUOTE_SENT"),
      icon: <MdFlashOn size={12} />,
    },
    [SUBSCRIPTION_STATUSES.PENDING]: {
      label: tTask("DETAIL.ACTIVITIES.SUBSCRIPTION_REQUEST_SENT"),
      icon: <MdFlashOn size={12} />,
    },
    [SUBSCRIPTION_STATUSES.SIGNED]: {
      label: tTask("DETAIL.ACTIVITIES.CONTRACT_SIGNED"),
      icon: <MdFlashOn size={12} />,
    },
    [SUBSCRIPTION_STATUSES.IN_PROGRESS]: {
      label: tTask("DETAIL.SUBSCRIPTION_STATUSES.IN_PROGRESS"),
      icon: <MdFlashOn size={12} />,
    },
  };
  if (bySubscriptionStatus[status]) {
    return bySubscriptionStatus[status];
  }

  const userActivityActionToTaskKey = {
    addQuote: "DETAIL.ACTIVITIES.QUOTE_SENT",
    sendQuote: "DETAIL.ACTIVITIES.QUOTE_SENT",
    generateQuoteDocuments: "DETAIL.ACTIVITIES.QUOTE_SENT",
    signDocuments: "DETAIL.ACTIVITIES.SUBSCRIPTION_REQUEST_SENT",
    documentsSigned: "DETAIL.ACTIVITIES.CONTRACT_SIGNED",
    electronicSignatureCompleted:
      "DETAIL.ACTIVITIES.ELECTRONIC_SIGNATURE_COMPLETED",
    manualSignatureCompleted: "DETAIL.ACTIVITIES.MANUAL_SIGNATURE_COMPLETED",
    projectDistributed: "DETAIL.ACTIVITIES.PROJECT_DISTRIBUTED",
    projectTakenOver: "DETAIL.ACTIVITIES.PROJECT_TAKEN_OVER",
    projectCreated: "DETAIL.ACTIVITIES.PROJECT_CREATED",
    quoteSent: "DETAIL.ACTIVITIES.QUOTE_SENT",
    quoteFollowUpSent: "DETAIL.ACTIVITIES.QUOTE_FOLLOW_UP_SENT",
    signatureInitiated: "DETAIL.ACTIVITIES.SIGNATURE_INITIATED",
    sentToManagement: "DETAIL.ACTIVITIES.SENT_TO_MANAGEMENT",
    sentToManagementManually: "DETAIL.ACTIVITIES.SENT_TO_MANAGEMENT_MANUALLY",
    projectDuplicated: "DETAIL.ACTIVITIES.PROJECT_DUPLICATED",
    projectCreatedFromSource: "DETAIL.ACTIVITIES.PROJECT_CREATED_FROM_SOURCE",
    projectSourceChanged: "DETAIL.ACTIVITIES.PROJECT_SOURCE_CHANGED",
    emailDelivered: "DETAIL.ACTIVITIES.EMAIL_DELIVERED",
    emailOpened: "DETAIL.ACTIVITIES.EMAIL_OPENED",
    emailClicked: "DETAIL.ACTIVITIES.EMAIL_CLICKED",
    documentOpened: "DETAIL.ACTIVITIES.DOCUMENT_OPENED",
  };
  const taskKey = userActivityActionToTaskKey[status];
  if (taskKey) {
    return {
      label: tTask(taskKey),
      icon: <MdFlashOn size={12} />,
    };
  }

  return {
    label: status,
    icon: <MdFlag size={12} />,
  };
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}min ${String(secs).padStart(2, "0")}s`;
};

const formatCallDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = ensureUtcDate(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Paris",
  });
};

const CallActivityItem = ({ activity, activityLinks }) => {
  const callData = activity.callData;
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const isAnswered = !!callData.answeredAt;
  const isPending = callData.status === CALL_STATUSES.PENDING;
  const isAnsweredByVoicemail = callData.answeredByVoicemail === true;

  const [fetchRecordingUrl] = useLazyQuery(CALL_RECORDING_URL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setLoadingRecording(false);
      const result = data?.callRecordingUrl;
      if (result?.url) {
        window.open(result.url, "_blank");
      } else if (result?.error) {
        notification.error({
          message: result.error,
          closeIcon: null,
        });
      }
    },
    onError: () => {
      setLoadingRecording(false);
      notification.error({
        message: t("ACTIVITIES.CALL.RECORDING_ERROR"),
        closeIcon: null,
      });
    },
  });

  const handleListenClick = () => {
    if (loadingRecording) return;
    setLoadingRecording(true);
    fetchRecordingUrl({ variables: { callId: callData.id } });
  };

  const title = isAnsweredByVoicemail
    ? t("ACTIVITIES.CALL.ANSWERED_BY_VOICEMAIL")
    : isPending
      ? t("ACTIVITIES.CALL.INITIATED")
      : isAnswered
        ? t("ACTIVITIES.CALL.ANSWERED")
        : t("ACTIVITIES.CALL.MISSED");

  const directionLabel =
    callData.direction === CALL_DIRECTIONS.INBOUND
      ? t("ACTIVITIES.CALL.INBOUND")
      : t("ACTIVITIES.CALL.OUTBOUND");

  const iconClass = isAnsweredByVoicemail
    ? "voicemail"
    : isPending
      ? "pending"
      : isAnswered
        ? "answered"
        : "missed";

  const callUser = callData.user;
  const nameFromUserRelation =
    callUser?.firstname || callUser?.lastname
      ? `${callUser?.firstname || ""} ${callUser?.lastname || ""}`.trim()
      : "";
  const advisorDisplayName = nameFromUserRelation || callData.userName || "";

  return (
    <div className="activities__log--item activities__log--call">
      <div className={classNames("icon", `call-icon--${iconClass}`)}>
        {isAnswered || isPending ? (
          <MdPhone size={12} />
        ) : (
          <MdPhoneMissed size={12} />
        )}
      </div>
      <div className="content">
        <div className="info">
          <div className="d-flex align--center gap-8">
            <span className="status">{title}</span>
            <span className="call-badge">AIRCALL</span>
          </div>
          <span className="call-details">
            {directionLabel} &middot; {callData.phoneNumber}
            {!isPending && callData.duration != null && (
              <>
                {" "}
                &middot; {t("ACTIVITIES.CALL.DURATION")} :{" "}
                {formatDuration(callData.duration)}
              </>
            )}
          </span>
          <ActivityIdentityNames
            activity={activity}
            activityLinks={activityLinks}
            actorLine={advisorDisplayName}
            t={t}
          />
          <div className="d-flex align--center gap-8">
            {callData.recordingAvailable && (
              <button
                className="call-listen-btn"
                onClick={handleListenClick}
                disabled={loadingRecording}
              >
                <MdHeadset size={12} />
                {loadingRecording
                  ? t("ACTIVITIES.CALL.LOADING")
                  : t("ACTIVITIES.CALL.LISTEN")}
              </button>
            )}
            {callData.aircallId && (
              <span className="call-aircall-id"># ac_{callData.aircallId}</span>
            )}
          </div>
        </div>
        <div className="activities__log--aside">
          <ActivityDashboardLinks
            activity={activity}
            activityLinks={activityLinks}
          />
          <span className="date">
            {formatCallDate(callData.startedAt || callData.dialedAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

CallActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
};

const EmailPreviewButton = ({ emailActivity, onOpenEmail }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  return (
    <button
      type="button"
      className="email-activity-preview-btn"
      onClick={() => onOpenEmail(emailActivity)}
    >
      <MdVisibility size={12} />
      {t("ACTIVITIES.EMAIL.PREVIEW")}
    </button>
  );
};

EmailPreviewButton.propTypes = {
  emailActivity: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  onOpenEmail: PropTypes.func.isRequired,
};

const getEmailActivityTitle = ({ subject }, t) =>
  subject || t("ACTIVITIES.EMAIL.SENT_TITLE");

const EmailSentActivityItem = ({ activity, activityLinks, onOpenEmail }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const { date, emailActivity } = activity;
  const { to, cc, user } = emailActivity || {};
  const toList = Array.isArray(to) ? to : to ? [to] : [];
  const toDisplay = toList.filter(Boolean).join(", ");
  const ccList = Array.isArray(cc) ? cc : [];
  const ccDisplay = ccList.filter(Boolean).join(", ");
  const senderName =
    user?.firstname || user?.lastname
      ? `${user?.firstname || ""} ${user?.lastname || ""}`.trim()
      : "";
  return (
    <div className="activities__log--item activities__log--email">
      <div className="icon email-activity-icon">
        <MdOutlineMail size={14} />
      </div>
      <div className="content">
        <div className="info">
          <span className="status">
            {getEmailActivityTitle(emailActivity || {}, t)}
          </span>
          {toDisplay ? (
            <span className="email-activity-details">
              {t("ACTIVITIES.EMAIL.TO")} : {toDisplay}
            </span>
          ) : null}
          {ccDisplay ? (
            <span className="email-activity-details">
              {t("ACTIVITIES.EMAIL.CC")} : {ccDisplay}
            </span>
          ) : null}
          <ActivityIdentityNames
            activity={activity}
            activityLinks={activityLinks}
            actorLine={senderName}
            t={t}
          />
        </div>
        <div className="activities__log--aside">
          <ActivityDashboardLinks
            activity={activity}
            activityLinks={activityLinks}
          />
          <span className="date">{date ? formatDate(date) : "-"}</span>
          {onOpenEmail && emailActivity?.id ? (
            <EmailPreviewButton
              emailActivity={emailActivity}
              onOpenEmail={onOpenEmail}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

EmailSentActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
  onOpenEmail: PropTypes.func,
};

EmailSentActivityItem.defaultProps = {
  onOpenEmail: undefined,
};

const INITIATOR_PROSPECT = "PROSPECT";
const INITIATOR_BROKER = "BROKER";
const INITIATOR_AUTOMATIC = "AUTOMATIC";

const getInitiatorBadgeLabel = (initiator, t) => {
  const type = initiator?.type;
  if (type === INITIATOR_PROSPECT) return t("ACTIVITIES.INITIATOR.PROSPECT");
  if (type === INITIATOR_BROKER) return t("ACTIVITIES.INITIATOR.BROKER");
  if (type === INITIATOR_AUTOMATIC) return t("ACTIVITIES.INITIATOR.AUTOMATIC");
  return "";
};

const getInitiatorName = (initiator) => {
  if (initiator?.type !== INITIATOR_BROKER) return "";
  return `${initiator?.firstname || ""} ${initiator?.lastname || ""}`.trim();
};

const formatPersonName = (person) =>
  `${person?.firstname || ""} ${person?.lastname || ""}`.trim();

const sourceNameOf = (source, t) =>
  get(source, "name") || t("ACTIVITIES.SOURCE.NONE");

const getSourceLine = ({ status, data }, t) => {
  if (status === "projectCreatedFromSource") {
    return t("ACTIVITIES.SOURCE.CURRENT", {
      current: sourceNameOf(get(data, "source"), t),
    });
  }
  if (status === "projectSourceChanged") {
    return t("ACTIVITIES.SOURCE.CHANGED", {
      previous: sourceNameOf(get(data, "from"), t),
      current: sourceNameOf(get(data, "to"), t),
    });
  }
  return "";
};

const StatusActivityItem = ({ activity, activityLinks }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const { t: tTask } = useTranslation(NAME_SPACES.PRIVATE.BROKER.TASK);
  const { status, date, author, activityType, initiator, recipient } = activity;
  const { label, icon } = getStatusActivityDisplay(status, tTask);
  const isQualification = activityType === ACTIVITY_TYPES.QUALIFICATION;
  const isSubscriptionStatus = activityType === ACTIVITY_TYPES.STATUS;
  const isDistributed = status === "projectDistributed";
  const badgeLabel = isQualification
    ? t("ACTIVITIES.FILTERS.QUALIFICATIONS")
    : t("ACTIVITIES.FILTERS.STATUSES");
  const initiatorBadgeLabel = initiator
    ? getInitiatorBadgeLabel(initiator, t)
    : "";
  const initiatorAdvisorLine = getInitiatorName(initiator);
  const recipientLine = recipient ? formatPersonName(recipient) : "";
  const sourceLine = getSourceLine(activity, t);
  const authorLine =
    !isDistributed && !initiatorAdvisorLine && !isEmpty(author)
      ? `${author?.firstname || ""} ${author?.lastname || ""}`.trim()
      : "";
  return (
    <div className="activities__log--item">
      <div
        className={classNames("icon", {
          "status-activity-icon--qualification": isQualification,
          "status-activity-icon--subscription": isSubscriptionStatus,
        })}
      >
        {icon}
      </div>
      <div className="content">
        <div className="info">
          <div className="d-flex align--center gap-8 flex--wrap">
            <span
              className={classNames("activities__log-type-badge", {
                "activities__log-type-badge--qualification": isQualification,
                "activities__log-type-badge--status": isSubscriptionStatus,
              })}
            >
              {badgeLabel}
            </span>
            <span className="status">{label}</span>
          </div>
          {initiatorBadgeLabel ? (
            <div className="activities__log--initiator-row d-flex align--center gap-8 flex--wrap mt-4">
              <span
                className={classNames(
                  "activities__log-type-badge",
                  "activities__log-type-badge--initiator",
                  {
                    "activities__log-type-badge--initiator-prospect":
                      initiator?.type === INITIATOR_PROSPECT,
                    "activities__log-type-badge--initiator-broker":
                      initiator?.type === INITIATOR_BROKER,
                    "activities__log-type-badge--initiator-automatic":
                      initiator?.type === INITIATOR_AUTOMATIC,
                  },
                )}
              >
                {initiatorBadgeLabel}
              </span>
              {initiatorAdvisorLine ? (
                <span className="activities__log--initiator-name">
                  {initiatorAdvisorLine}
                </span>
              ) : null}
            </div>
          ) : null}
          {isDistributed && recipientLine ? (
            <div className="activities__log--recipient-row mt-4">
              <span className="activities__log--recipient-line">
                {t("ACTIVITIES.DISTRIBUTED_TO")} {recipientLine}
              </span>
            </div>
          ) : null}
          {sourceLine ? (
            <div className="activities__log--recipient-row mt-4">
              <span className="activities__log--recipient-line">
                {sourceLine}
              </span>
            </div>
          ) : null}
          <ActivityIdentityNames
            activity={activity}
            activityLinks={activityLinks}
            actorLine={authorLine}
            t={t}
          />
        </div>
        <div className="activities__log--aside">
          <ActivityDashboardLinks
            activity={activity}
            activityLinks={activityLinks}
          />
          <span className="date">{date ? formatDate(date) : "-"}</span>
        </div>
      </div>
    </div>
  );
};

StatusActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
};

const whatsappTitle = ({ status, data }, t) => {
  if (status !== ALVIO_ACTIVITY_ACTIONS.EVENT_RECEIVED)
    return t(`ACTIVITIES.WHATSAPP.ACTIONS.${status}`);
  const event = get(data, "event");
  return t(`ACTIVITIES.WHATSAPP.EVENTS.${event}`, {
    defaultValue: t("ACTIVITIES.WHATSAPP.UNKNOWN_EVENT", { event }),
  });
};

const whatsappDetail = ({ status, data }, t) => {
  if (status === ALVIO_ACTIVITY_ACTIONS.SEND_FAILED)
    return [
      get(data, "error.message"),
      t(
        get(data, "retrying")
          ? "ACTIVITIES.WHATSAPP.RETRYING"
          : "ACTIVITIES.WHATSAPP.GAVE_UP",
      ),
    ]
      .filter(Boolean)
      .join(" · ");
  if (status === ALVIO_ACTIVITY_ACTIONS.FOLLOW_UP_CANCELLED)
    return t(`ACTIVITIES.WHATSAPP.CANCEL_REASONS.${get(data, "reason")}`);
  return "";
};

const WhatsappActivityItem = ({ activity, activityLinks }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const { date, data, status } = activity;
  const campaign = get(data, "campaign");
  const message = get(data, "message");
  const detail = whatsappDetail(activity, t);
  return (
    <div className="activities__log--item activities__log--whatsapp">
      <div
        className={classNames("icon whatsapp-activity-icon", {
          "whatsapp-activity-icon--failed":
            status === ALVIO_ACTIVITY_ACTIONS.SEND_FAILED,
        })}
      >
        <FaWhatsapp size={14} />
      </div>
      <div className="content">
        <div className="info">
          <div className="d-flex align--center gap-8 flex--wrap">
            <span className="activities__log-type-badge activities__log-type-badge--whatsapp">
              {t("ACTIVITIES.WHATSAPP.BADGE")}
            </span>
            <span className="status">{whatsappTitle(activity, t)}</span>
          </div>
          {campaign ? (
            <span className="whatsapp-activity-details">
              {t("ACTIVITIES.WHATSAPP.CAMPAIGN", {
                campaign: t(`ACTIVITIES.WHATSAPP.CAMPAIGNS.${campaign}`),
              })}
            </span>
          ) : null}
          {detail ? (
            <span className="whatsapp-activity-details">{detail}</span>
          ) : null}
          {message ? (
            <span className="whatsapp-activity-message">{message}</span>
          ) : null}
          <ActivityIdentityNames
            activity={activity}
            activityLinks={activityLinks}
            actorLine=""
            t={t}
          />
        </div>
        <div className="activities__log--aside">
          <ActivityDashboardLinks
            activity={activity}
            activityLinks={activityLinks}
          />
          <span className="date">{date ? formatDate(date) : "-"}</span>
        </div>
      </div>
    </div>
  );
};

WhatsappActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
};

const ActivityItem = ({ activity, activityLinks, onOpenEmail }) => {
  if (activity.activityType === ACTIVITY_TYPES.CALL && activity.callData) {
    return (
      <CallActivityItem activity={activity} activityLinks={activityLinks} />
    );
  }
  if (
    activity.activityType === ACTIVITY_TYPES.EMAIL_SENT &&
    activity.emailActivity
  ) {
    return (
      <EmailSentActivityItem
        activity={activity}
        activityLinks={activityLinks}
        onOpenEmail={onOpenEmail}
      />
    );
  }
  if (activity.activityType === ACTIVITY_TYPES.WHATSAPP) {
    return (
      <WhatsappActivityItem activity={activity} activityLinks={activityLinks} />
    );
  }
  return (
    <StatusActivityItem activity={activity} activityLinks={activityLinks} />
  );
};

ActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
  activityLinks: activityLinksPropType,
  onOpenEmail: PropTypes.func,
};

ActivityItem.defaultProps = {
  onOpenEmail: undefined,
};

export default ActivityItem;
